"use client"

import Backdrop from "@components/Backdrop"
import FilterTag, { ContainerFilled, Icon } from "@components/FilterTag"
import { Close as CloseIcon, Search as SearchIcon } from "@components/icons"
import SearchBlock, { SearchBlockProps } from "@components/SearchBlock"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body16MainStyles,
  h4Styles,
  smallcapsBold13Styles,
} from "@constants/typography"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import _ from "lodash-es"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

import Suggestions, { SuggestionProps } from "./Suggestions"

const openProps = `
  opacity: 1;
  transform: translateY(0);
  transition: opacity ${animation.quick} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
`
const Container = styled.div<{ $isOpen: boolean; $showPlaceholder: boolean }>`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  flex-direction: column;
  opacity: 0;
  transform: translateY(-100%);
  transition:
    opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  overflow: ${({ $showPlaceholder }) =>
    $showPlaceholder ? "visible" : "hidden"};
  cursor: default;
  z-index: 1001;
  ${({ $isOpen }) => ($isOpen ? openProps : "")}

  @media ${breakpoints.laptop} {
    padding-top: 53px;
  }
`

const searchWrapperStyles = `
  display: flex;
  padding-left: 20px;
  align-items: center;
  border-bottom: solid 1px ${colors.dividerLineGray};
  z-index: 1;

  @media ${breakpoints.tablet} {
    padding-left: 32px;
  }

  @media ${breakpoints.laptop} {
    height: 52px;
    padding-left: calc(2 / 12 * 100vw);
    border: none;
  }
`

const SearchWrapperMobileAndTablet = styled.div`
  ${searchWrapperStyles}

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const SearchWrapperDesktop = styled.div`
  ${searchWrapperStyles}
  display: none;

  @media ${breakpoints.laptop} {
    display: flex;
  }
`

const SearchIconWrapper = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  display: flex;
  width: 24px;
  height: 24px;
`

const InputWrapper = styled.div`
  flex-grow: 1;
  flex-shrink: 1;

  @media ${breakpoints.laptop} {
    width: calc((8 / 12 * 100vw) + 24px);
    padding-right: calc(1 / 12 * 100vw);
    flex-grow: 0;
  }
`

// Interstate padding
const placeholderStyles = `
  color ${colors.mediumGray};
  opacity: 1;

  @media ${breakpoints.mobileAndTabletOnly} {
    line-height: normal !important;
  }

  @media ${breakpoints.laptop} {
    opacity: 0.5;
  }
`
const Input = styled.input`
  ${body16MainStyles}
  width: 100%;
  height: 64px;
  padding-left: 13px;
  border: none;
  &:active {
    outline: none;
  }
  &:focus {
    outline: none;
  }

  ::-webkit-input-placeholder {
    ${placeholderStyles}
  }
  ::-moz-placeholder {
    ${placeholderStyles}
  }
  :-ms-input-placeholder {
    ${placeholderStyles}
  }
  :-moz-placeholder {
    ${placeholderStyles}
  }

  @media ${breakpoints.laptop} {
    ${h4Styles}
    padding-top: 4px;
    padding-left: 17px;
  }
`

const ClearButton = styled.button`
  display: flex;
  height: 64px;
  width: 56px;
  flex-grow: 0;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  &:focus {
    outline: none;
  }

  @media ${breakpoints.laptop} {
    &:hover {
      cursor: pointer;
    }
  }
`

const ClearIconWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${colors.dividerLineGray};
`

const DoneButton = styled.button`
  ${body16MainStyles}
  width: 88px;
  height: 64px;
  flex-grow: 0;
  flex-shrink: 0;
  color: ${colors.mediumGray};
  background-color: transparent;
  border: none;
  border-left: solid 1px ${colors.mediumGray};
  border-left: solid 1px ${colors.dividerLineGray};
  &:focus {
    outline: none;
  }

  @media ${breakpoints.laptop} {
    &:hover {
      cursor: pointer;
    }
  }
`

const CloseButtonWrapper = styled.div`
  width: calc(1 / 12 * 100vw);
  padding-left: 6px;
  align-items: center;
  justify-content: flex-start;
`

const CloseButton = styled.button`
  display: flex;
  width: 56px;
  height: 56px;
  flex-grow: 0;
  align-items: center;
  justify-content: center;
  background-color: ${colors.dividerLineGray};
  border: none;
  &:focus {
    outline: none;
  }

  @media ${breakpoints.laptop} {
    &:hover {
      cursor: pointer;
    }
  }
`

const CloseIconWrapper = styled.div`
  width: 40px;
  height: 40px;
`

const placeholderVisibleProps = `
  opacity: 1;
  transition: opacity ${animation.quick} ${cubicBezier};
`
const ResultsPlaceholder = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh;
  opacity: 0;
  transition: opacity ${animation.quick} ${cubicBezier} ${animation.quick};
  background-color: ${colors.offWhite};

  ${({ $isVisible }) => ($isVisible ? placeholderVisibleProps : "")}
`

const Results = styled.div`
  min-height: calc(100vh - 66px);
  padding: 36px 20px 28px;
  z-index: 1;

  @media ${breakpoints.tablet} {
    padding: 44px 32px 28px;
  }

  @media ${breakpoints.laptop} {
    min-height: calc(100vh - 105px);
    padding: 58px calc(2 / 12 * 100vw) 176px;
  }
`

const Title = styled.div`
  ${smallcapsBold13Styles}
`

const Categories = styled.div`
  display: flex;
  margin-top: 20px;

  @media ${breakpoints.mobileOnly} {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    scrollbar-width: none;

    ::-webkit-scrollbar {
      display: none;
    }
  }

  @media ${breakpoints.laptop} {
    flex-wrap: wrap;
  }
`

const CategoryWrapper = styled.div`
  margin-right: 8px;
  flex-shrink: 0;

  ${ContainerFilled} {
    height: 48px;
    padding-left: 12px !important;
    padding-right: 12px !important;
    align-items: center;
    justify-content: center;
  }

  @media ${breakpoints.laptop} {
    margin-right: 24px;
    margin-bottom: 12px;

    ${ContainerFilled} {
      height: 64px;
      padding-left: 24px !important;
      padding-right: 24px !important;
      transition: background-color ${animation.medium} ${cubicBezier};

      &:hover {
        background-color: ${colors.lightGray};
        transition: background-color ${animation.quick} ${cubicBezier};
      }
    }
  }

  ${Icon} {
    display: none;
  }
`

const Products = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
`

const ProductWrapper = styled.div`
  width: calc((1 / 2 * 100vw) - 30px);
  margin-bottom: 30px;
  flex-shrink: 0;

  @media ${breakpoints.tablet} {
    width: calc((1 / 2 * 100vw) - 48px);
    margin-bottom: 32px;
  }

  @media ${breakpoints.laptop} {
    width: calc((2 / 12 * 100vw) - 18px);
  }
`

interface SearchProps {
  isOpen: boolean
  lastSearchResult: string
  onChange?: (event: any) => void
  onClickSuggestion?: (suggestion: string) => void
  onKeyDown?: (event: any) => void
  onRequestClear?: () => void
  onRequestClose?: () => void
  searchCategories?: Array<{ text: string; to: string }>
  searchResults?: Array<SearchBlockProps>
  searchSuggestions: Array<SuggestionProps>
  searchValue: string
  setLastSearchResult: (lastSearchResult: string) => void
}

const backdropRef = React.createRef<HTMLDivElement>()
const resultsRef = React.createRef<HTMLDivElement>()
const createResultSpacer = () => <ProductWrapper key={_.uniqueId()} />

let timeout: number | undefined = undefined
const Search: React.FC<SearchProps> = ({
  isOpen,
  lastSearchResult,
  onChange,
  onClickSuggestion,
  onKeyDown,
  onRequestClear,
  onRequestClose,
  searchCategories = [],
  searchResults = [],
  searchSuggestions = [],
  searchValue,
  setLastSearchResult,
}: SearchProps) => {
  const modalRef: any = React.useRef(null)

  useModalBackgroundClose({
    isOpen,
    modalRef,
    onRequestClose,
  })

  const mobileInputRef = React.useRef<HTMLInputElement>(null)
  const desktopInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    if (
      mobileInputRef &&
      mobileInputRef.current &&
      mobileInputRef.current.getBoundingClientRect().height > 0
    ) {
      mobileInputRef.current.focus()
    } else if (
      desktopInputRef &&
      desktopInputRef.current &&
      desktopInputRef.current.getBoundingClientRect().height > 0
    ) {
      desktopInputRef.current.focus()
    }
  }, [isOpen])

  const [pristine, setIsPristine] = React.useState(true)

  React.useEffect(() => {
    if (isOpen) {
      setIsPristine(true)
      setRenderedResultCount(20)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (pristine && !R.isEmpty(searchValue)) {
      setIsPristine(false)
    }
  }, [searchValue])

  const [renderedResultCount, setRenderedResultCount] = React.useState(20)
  const [validResults, setValidResults] = React.useState([] as any)

  React.useEffect(() => {
    setValidResults(R.reject(({ imageUrl }) => !imageUrl)(searchResults))
  }, [searchResults])

  const onScrollHandler = () => {
    if (
      typeof window === "undefined" ||
      !resultsRef ||
      !resultsRef.current ||
      validResults.length <= renderedResultCount
    ) {
      return
    }

    const resultNodes = resultsRef.current.childNodes
    const positionReference: any = resultNodes[resultNodes.length - 4]

    if (positionReference.getBoundingClientRect().top < window.innerHeight) {
      setRenderedResultCount(renderedResultCount + 20)
    }
  }

  React.useEffect((): any => {
    if (backdropRef && backdropRef.current) {
      backdropRef.current.addEventListener("scroll", onScrollHandler)
    }

    return () => {
      if (backdropRef && backdropRef.current) {
        backdropRef.current.removeEventListener("scroll", onScrollHandler)
      }
    }
  }, [isOpen, validResults, renderedResultCount])

  const [renderedResults, setRenderedResults] = React.useState([] as any)

  React.useEffect(() => {
    setRenderedResults(R.take(renderedResultCount)(validResults))
  }, [renderedResultCount, validResults])

  const { elevarOnViewSearchResults } = useElevarEvents()

  const sendOnViewSearchResultsOnce = (elevarProducts) => {
    const skuString = elevarProducts.map(({ sku }) => sku).join("")

    if (!lastSearchResult || lastSearchResult !== skuString) {
      setLastSearchResult(skuString)
      elevarOnViewSearchResults(elevarProducts)
    }
  }

  const handleElevarOnViewSearchResults = _.debounce(
    sendOnViewSearchResultsOnce,
    400,
    {
      leading: true,
      maxWait: 500,
      trailing: true,
    }
  )

  const clearKeydownTimeouts = () => {
    if (timeout && typeof window !== "undefined") {
      window.clearTimeout(timeout)
      timeout = undefined
    }
  }

  React.useEffect(() => {
    if (renderedResults.length) {
      if (typeof window !== "undefined") {
        const elevarProducts = renderedResults.map((p, i) => ({
          ...p.elevarProduct,
          position: i + 1,
        })) as Array<ElevarProductProps>

        clearKeydownTimeouts()
        const callElevarEvent = window.setTimeout(() => {
          handleElevarOnViewSearchResults(elevarProducts)
        }, 1000)
        timeout = callElevarEvent
      }
    }
  }, [renderedResults])

  return (
    <Backdrop $isOpen={isOpen} ref={backdropRef}>
      <Container $isOpen={isOpen} $showPlaceholder={!pristine} ref={modalRef}>
        <SearchWrapperMobileAndTablet>
          <SearchIconWrapper>
            <SearchIcon strokeColor={colors.black} />
          </SearchIconWrapper>
          <InputWrapper>
            <Input
              onChange={onChange}
              onKeyDown={onKeyDown}
              placeholder="Search Products"
              ref={mobileInputRef}
              value={searchValue}
            />
          </InputWrapper>
          <ClearButton onClick={onRequestClear}>
            <ClearIconWrapper>
              <CloseIcon strokeColor={colors.mediumDarkGray} strokeWidth="4" />
            </ClearIconWrapper>
          </ClearButton>
          <DoneButton onClick={onRequestClose}>Close</DoneButton>
        </SearchWrapperMobileAndTablet>
        <SearchWrapperDesktop>
          <SearchIconWrapper>
            <SearchIcon strokeColor={colors.black} />
          </SearchIconWrapper>
          <InputWrapper>
            <Input
              onChange={onChange}
              onKeyDown={onKeyDown}
              placeholder="Search by Keyword or Category"
              ref={desktopInputRef}
              value={searchValue}
            />
          </InputWrapper>
          <CloseButtonWrapper>
            <CloseButton onClick={onRequestClose}>
              <CloseIconWrapper>
                <CloseIcon strokeColor={colors.mediumDarkGray} />
              </CloseIconWrapper>
            </CloseButton>
          </CloseButtonWrapper>
        </SearchWrapperDesktop>
        {!validResults || R.isEmpty(validResults) ? (
          <Suggestions
            onClickSuggestion={onClickSuggestion}
            searchSuggestions={searchSuggestions}
          />
        ) : (
          <Results>
            <Title>Categories</Title>
            <Categories>
              {searchCategories.map(({ text, to }) => (
                <CategoryWrapper key={_.uniqueId()}>
                  <FilterTag text={text} to={to} />
                </CategoryWrapper>
              ))}
            </Categories>
            <Products ref={resultsRef}>
              {renderedResults.map((product: SearchBlockProps) => (
                <ProductWrapper key={product.slug}>
                  <SearchBlock
                    imageUrl={product.imageUrl}
                    name={product.name}
                    onClick={product.onClick}
                    price={product.price}
                    slug={product.slug}
                    tags={product.tags}
                  />
                </ProductWrapper>
              ))}
              {!R.isEmpty(renderedResults) &&
                R.times(createResultSpacer, 4 - (renderedResults.length % 4))}
            </Products>
          </Results>
        )}
        <ResultsPlaceholder $isVisible={!pristine} />
      </Container>
    </Backdrop>
  )
}

export default Search
